import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const VideoPlayer = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isPlaying, setIsPlaying] = useState(false);

  // Get video data from navigation state
  const { video, description, title, flag, courseId } = location.state || {};

  console.log('VideoPlayer state:', { video, description, title, flag, courseId });

  // Check if not purchased (flag === 1)
  if (flag === 1) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Purchase Required
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Please purchase this course to watch videos.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-6">❌</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Video Not Found</h2>
          <p className="text-gray-500 mb-6">The video URL is not available</p>
          <button 
            onClick={() => navigate(-1)}
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Determine if it's a YouTube video
  // Mobile app stores just the video ID (11 characters like "Op24cepv26I")
  let youtubeVideoId = null;
  let isYouTube = false;
  
  // Check if it's just a YouTube video ID (11 characters, alphanumeric with _ and -)
  const isJustVideoId = /^[a-zA-Z0-9_-]{11}$/.test(video);
  
  if (isJustVideoId) {
    // It's just the YouTube video ID (matching mobile app behavior)
    youtubeVideoId = video;
    isYouTube = true;
    console.log('✅ Detected as YouTube video ID:', youtubeVideoId);
  } else if (video.includes('youtube.com') || video.includes('youtu.be')) {
    // It's a full YouTube URL - extract the ID
    isYouTube = true;
    
    // Multiple regex patterns to catch different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,           // youtube.com/watch?v=VIDEO_ID
      /(?:youtube\.com\/embed\/)([^?&\s]+)/,            // youtube.com/embed/VIDEO_ID
      /(?:youtu\.be\/)([^?&\s]+)/,                      // youtu.be/VIDEO_ID
      /(?:youtube\.com\/v\/)([^?&\s]+)/,                // youtube.com/v/VIDEO_ID
      /(?:youtube\.com\/.*[?&]v=)([^&\s]+)/,            // Any youtube.com with v= parameter
    ];
    
    for (const pattern of patterns) {
      const match = video.match(pattern);
      if (match && match[1]) {
        youtubeVideoId = match[1];
        console.log('✅ Extracted YouTube ID from URL:', youtubeVideoId);
        break;
      }
    }
  }
  
  console.log('📹 Video processing:', { 
    originalValue: video, 
    isJustVideoId,
    isYouTube, 
    youtubeVideoId,
    willUseYouTubePlayer: !!youtubeVideoId
  });

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Video Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)}
              className="text-white hover:text-gray-300 flex items-center gap-2"
            >
              <span className="text-2xl">←</span>
              <span>Back</span>
            </button>
            <h1 className="text-white text-lg font-semibold flex-1 text-center px-4 line-clamp-1">
              {title || 'Video Player'}
            </h1>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Video Player */}
      <div className="container-fluid py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Video Container - Increased size */}
          <div className="bg-black rounded-lg overflow-hidden shadow-2xl mb-6">
            <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              {youtubeVideoId ? (
                // YouTube Player (fullscreen disabled)
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1&fs=0`}
                  title={title || 'Video Player'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  onLoad={() => setIsPlaying(true)}
                ></iframe>
              ) : isYouTube ? (
                // Fallback: If YouTube detected but ID not extracted, try iframe with full URL
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={video}
                  title={title || 'Video Player'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  onLoad={() => setIsPlaying(true)}
                ></iframe>
              ) : (
                // HTML5 Video Player (for direct video files, fullscreen disabled)
                <video
                  className="absolute top-0 left-0 w-full h-full"
                  controls
                  autoPlay
                  controlsList="nodownload nofullscreen"
                  disablePictureInPicture
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                >
                  <source src={video} type="video/mp4" />
                  <source src={video} type="video/webm" />
                  <source src={video} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>

          {/* Video Info */}
          <div className="bg-gray-800 rounded-lg p-6 shadow-lg">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-bold text-white flex-1">
                {title || 'Video'}
              </h2>
              {/* Video Type Badge */}
              {youtubeVideoId ? (
                <span className="flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube
                </span>
              ) : (
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Direct Video
                </span>
              )}
            </div>
            
            {description && (
              <div className="text-gray-300">
                <h3 className="text-lg font-semibold mb-2">Description</h3>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                  {description}
                </div>
              </div>
            )}

            {/* Debug Info */}
            <div className="mt-6 pt-6 border-t border-gray-700">
              <details className="text-xs text-gray-400">
                <summary className="cursor-pointer hover:text-gray-300">Video Details</summary>
          
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;

